/**
 * SanPlumb — Utilidades puras de persistencia en `sessionStorage` (§II)
 *
 * El baseline §II/§XV restringe el storage del cliente a `sessionStorage`
 * únicamente. Estas funciones encapsulan la lectura/escritura JSON con
 * tolerancia a:
 *   - storage indisponible (Safari private, SSR)
 *   - JSON corrupto (regreso al default sin lanzar)
 *   - serialización fallida (errores de circular, etc.)
 *
 * El parámetro `storage` se inyecta para que los tests pasen un Storage
 * mock. En runtime, `useSessionState` usa `window.sessionStorage`.
 *
 * Convención de keys: 'sanplumb:<feature>:<subkey>' (ver SESSION_KEYS).
 */

/** Lee un valor JSON del storage o devuelve el default si falla. */
export function readJson<T>(storage: Storage | null, key: string, def: T): T {
  if (!storage) return def;
  try {
    const raw = storage.getItem(key);
    if (raw == null) return def;
    return JSON.parse(raw) as T;
  } catch {
    return def;
  }
}

/** Escribe un valor JSON al storage. Silencioso si falla. */
export function writeJson(storage: Storage | null, key: string, val: unknown): void {
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(val));
  } catch {
    // QuotaExceeded, private mode, etc. — no es fatal, el estado React sigue vivo.
  }
}

/** Borra una key del storage. Silencioso si falla. */
export function removeJson(storage: Storage | null, key: string): void {
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    // idem
  }
}

/** Keys de sessionStorage usadas por la app. Centralizadas para evitar typos. */
export const SESSION_KEYS = {
  HUNTER_COUNTS: 'sanplumb:hunter:counts',
  BRAND: 'sanplumb:viewer:brand',
  ROUTING: 'sanplumb:viewer:routing',
  TIPOLOGIA: 'sanplumb:hunter:tipologia',
  ROOM_DIMS: 'sanplumb:ingesta:roomDims',
  LAYERS: 'sanplumb:viewer:layers',
  AUTO_ROTATE: 'sanplumb:viewer:autoRotate',
  DOTACION: 'sanplumb:dotacion:form',
  DEPOSITOS: 'sanplumb:dotacion:depositos',
  PRESSURE_PARAMS: 'sanplumb:pressure:params',
  PRESSURE_OVERRIDES: 'sanplumb:pressure:overrides',
} as const;
